/* eslint-disable react/no-unused-prop-types */
import classNames from 'classnames';
import equal from 'fast-deep-equal';
import invariant from 'invariant';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

import { UiFilterPopover } from 'src/components/UI/FilterPopover';
import { UiPagination } from 'src/components/UI/Pagination';
import {
  UiTableActionsPartial,
  UiTableCheckbox,
  UiTableColumnSort,
  UiTableColumnType,
  UiTableCreateTH,
  uiTableGetColumnDataWithItem,
  UiTableGetInitialFilterValues,
  UiTableInnerFiltersType,
  UiTableLoadingPartial,
  UiTableNotFoundPartial,
  UiTableRefProps,
} from 'src/components/UI/table/UiTable.helpers';
import { UiTextTooltip } from 'src/components/UI/TextTooltip';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/config';
import { BaseTableFilterFiltersType, CommonTableResponseType } from 'src/models/Table';
import { BaseStoreEntityType } from 'src/store/types';
import { scrollWindowToTop } from 'src/utils/DOM';
import { showAnyErrors } from 'src/utils/errors';
import { getUniqueId } from 'src/utils/getUniqueId';
import { useClickOutside } from 'src/utils/hooks/useClickOutside';
import { useMediaQuery } from 'src/utils/hooks/useMediaQuery';
import { useMemoObject } from 'src/utils/hooks/useMemoObject';
import { useQuery } from 'src/utils/hooks/useQuery';
import { numberOrDefaultNumber } from 'src/utils/numbers';
import { SafeAnyType } from 'src/utils/safeAny';
import { useStateForm } from 'src/utils/stateForm';
import { ChildrenPropType } from 'src/utils/types';
import { getSearchParams, setSearchParam } from 'src/utils/url';
import styles from './styles.module.scss';

const filterProperties = [
  'PageSize',
  'PageIndex',
  'SortByProperty',
  'SortDirection',
  'Filters',
  'CheckboxFilters',
  'OnlyFlagged',
];

export type UiTableProps = {
  data: BaseStoreEntityType<CommonTableResponseType<SafeAnyType>>;
  getData: (filter: UiTableInnerFiltersType) => void;
  columns: UiTableColumnType[];

  actions?: (item: SafeAnyType) => React.ReactNode;
  itemsPerPage?: number;
  notFoundText?: string;
  dummyTable?: boolean;
  manageActions?: boolean;
  className?: string;
  initialFilters?: BaseTableFilterFiltersType;
  itemKeyProp?: string; // always must be unique
  footer?: ChildrenPropType;
  doSetSearchParams?: boolean;
  saveHistory?: boolean;
  notCallSubmitWhenPropertyIsEmpty?: keyof UiTableInnerFiltersType;
  disabledScrollToTop?: boolean;
  withoutSearchParams?: boolean;
  v2?: boolean;
  trClassName?: string;
  onTrClick?: (item: SafeAnyType) => () => void;
  fullTableContent?: (data: { colSpan: number }) => React.ReactNode;
  wrapperClassName?: string;
  wrapperTableScrollerClassName?: string;
};

export const UiTable = memo(
  forwardRef<UiTableRefProps, UiTableProps>(
    (
      {
        columns,
        getData,
        data,
        actions,
        itemsPerPage = DEFAULT_TABLE_PAGE_SIZE,
        notFoundText,
        dummyTable = false,
        manageActions = false,
        className,
        initialFilters = [],
        itemKeyProp = 'ID',
        footer,
        doSetSearchParams = true,
        saveHistory = true,
        notCallSubmitWhenPropertyIsEmpty,
        disabledScrollToTop = false,
        withoutSearchParams,
        v2 = false,
        trClassName,
        onTrClick,
        fullTableContent,
        wrapperClassName,
        wrapperTableScrollerClassName,
      }: UiTableProps,
      ref
    ) => {
      const formProps = useStateForm();

      const localColumns = useMemo(
        () =>
          columns.filter((item) => {
            if (isFunction(item)) {
              return true;
            }
            return !item.useSortAndNotShow;
          }),
        [columns]
      );

      const { reset } = formProps;

      const queryPageIndex = useQuery('PageIndex');

      const initialFiltersRef = useRef<UiTableInnerFiltersType>();
      const initialFormPropsFiltersRef = useRef<SafeAnyType>();

      /** handle actions menu */
      const [openMenuId, setOpenMenuId] = useState<number | null>(null);

      const cloneObject = useCallback(
        (value: Record<SafeAnyType, SafeAnyType>) => JSON.parse(JSON.stringify(value)),
        []
      );

      const filtersRef = useRef<UiTableInnerFiltersType>();
      /** get and store initial filters */
      const [filters, setFiltersState] = useState<UiTableInnerFiltersType>(() => {
        const preparedFilters = UiTableGetInitialFilterValues({
          params: withoutSearchParams ? {} : getSearchParams(),
          columns,
          initialFilters,
          itemsPerPage,
        });

        /** set formValues of filters */
        preparedFilters.Filters.forEach((filterItem) => {
          if (filterItem.Value) {
            formProps.setValue(filterItem.PropertyName, filterItem.Value);
          } else if (filterItem.ValueFrom || filterItem.ValueTo) {
            formProps.setValue(filterItem.PropertyName, [filterItem.ValueFrom, filterItem.ValueTo]);
          }
        });

        const initialFiltersPrepared = UiTableGetInitialFilterValues({
          params: {},
          columns,
          initialFilters,
          itemsPerPage,
        });
        initialFiltersRef.current = initialFiltersPrepared;
        initialFormPropsFiltersRef.current = initialFiltersPrepared.Filters.reduce(
          (acc: Record<string, SafeAnyType>, filterItem) => {
            if (filterItem.Value) {
              acc[filterItem.PropertyName] = filterItem.Value;
            } else if (filterItem.ValueFrom || filterItem.ValueTo) {
              acc[filterItem.PropertyName] = [filterItem.ValueFrom, filterItem.ValueTo];
            }
            return acc;
          },
          {}
        );

        filtersRef.current = cloneObject(preparedFilters);
        return cloneObject(preparedFilters);
      });

      const memoizedFilters = useMemoObject(filters);

      const doPushHistoryRef = useRef(true);

      const setFilters = useCallback(
        (
          filters: UiTableInnerFiltersType | ((prev: UiTableInnerFiltersType) => UiTableInnerFiltersType),
          pushHistory = true
        ) => {
          setFiltersState((prevState) => {
            const newValue = isFunction(filters) ? filters(cloneObject(prevState)) : filters;

            if (filtersRef.current && !equal(filtersRef.current, newValue)) {
              doPushHistoryRef.current = pushHistory;
            }

            filtersRef.current = cloneObject(newValue);
            return cloneObject(newValue);
          });
        },
        [cloneObject]
      );

      const history = useHistory();

      useEffect(() => {
        if (!saveHistory || !doSetSearchParams) {
          return () => null;
        }

        const unListen = history.listen((_, action) => {
          const preparedSearchFilters = UiTableGetInitialFilterValues({
            params: withoutSearchParams ? {} : getSearchParams(),
            columns,
            // initialFilters, // if initialFilters set it doesn't work when filters change (get params)
            itemsPerPage,
          });
          const preparedStateFilters = UiTableGetInitialFilterValues({
            params: {
              ...filtersRef.current,
              PageIndex: (filtersRef.current?.PageIndex || 0) + 1,
            } as SafeAnyType,
            columns,
            // initialFilters,
            itemsPerPage,
          });

          if (!equal(preparedSearchFilters, preparedStateFilters)) {
            setFilters(preparedSearchFilters, action === 'PUSH');
          }
        });

        return () => unListen();
      }, [
        columns,
        doSetSearchParams,
        history,
        initialFilters,
        itemsPerPage,
        saveHistory,
        setFilters,
        withoutSearchParams,
      ]);

      /** sync current page if something else changes query params */
      useEffect(() => {
        if (queryPageIndex && !withoutSearchParams) {
          /** !!do not set the new filter value if PageIndex equals to queryPageIndex!! */
          setFilters((currentFilters) =>
            currentFilters.PageIndex === +queryPageIndex - 1
              ? currentFilters
              : {
                  ...currentFilters,
                  PageIndex: +queryPageIndex - 1,
                }
          );
        }
      }, [queryPageIndex, setFilters, withoutSearchParams]);

      /** a function for outer changing filters */
      const changeFilters = useCallback<UiTableRefProps['changeFilters']>(
        (newValues, clearFilters) => {
          setFilters((currentValues) => {
            const newFilters = newValues?.Filters || [];
            const ignoreCurrentFiltersValuesAsTheyAreInNewFilters = newFilters.map(({ PropertyName }) => PropertyName);

            const filters = clearFilters
              ? newFilters
              : [
                  ...currentValues.Filters.filter(
                    ({ PropertyName }) => !ignoreCurrentFiltersValuesAsTheyAreInNewFilters.includes(PropertyName)
                  ),
                  ...newFilters,
                ];

            /** it's better to check every time then set new state even when the old one is equal to the new one */
            const possibleNewValues: UiTableInnerFiltersType = cloneObject({
              ...currentValues,
              ...newValues,
              Filters: filters,
            });

            /**
             * you may pass Filters with `Value: undefined` to remove them from the inner filters of the table
             * remember there are filters containing ValueFrom or/and ValueTo
             * */
            newFilters.forEach((item) => {
              if (
                (item.Value === null || item.Value === undefined) &&
                item.ValueFrom === undefined &&
                item.ValueTo === undefined
              ) {
                possibleNewValues.Filters = possibleNewValues.Filters.filter(
                  ({ PropertyName }) => PropertyName !== item.PropertyName
                );
              }
            });

            return equal(currentValues, possibleNewValues) ? currentValues : possibleNewValues;
          });
        },
        [cloneObject, setFilters]
      );

      const reloadData = useCallback(() => {
        getData({
          PageSize: memoizedFilters.PageSize,
          PageIndex: memoizedFilters.PageIndex,
          SortByProperty: memoizedFilters.SortByProperty,
          SortDirection: memoizedFilters.SortDirection,
          Filters: memoizedFilters.Filters,
          CheckboxFilters: [],
          OnlyFlagged: memoizedFilters.OnlyFlagged,
        });
      }, [memoizedFilters, getData]);

      const getFilters = useCallback(() => filtersRef.current, []);

      const resetFilters = useCallback(() => {
        if (initialFiltersRef.current) {
          changeFilters(initialFiltersRef.current);
          reset(initialFormPropsFiltersRef.current);
        }
      }, [changeFilters, reset]);

      useImperativeHandle(ref, () => ({ changeFilters, reloadData, getFilters }));

      const onSubmitFilter = useCallback(
        ({ PropertyName, Value, ValueFrom, ValueTo, FilterType }: BaseTableFilterFiltersType[0]) => {
          setFilters((v) => {
            const foundIndex = v.Filters.findIndex((item) => item.PropertyName === PropertyName);

            /** delete if the value is empty and is found in filter */
            if (!(Value || ValueFrom || ValueTo) && ~foundIndex) {
              v.Filters.splice(foundIndex, 1);
              v.PageIndex = 0;
              return cloneObject(v);
            }

            const filterValue: BaseTableFilterFiltersType[0] = {
              FilterType,
              PropertyName,
              Value,
              ValueFrom,
              ValueTo,
            };

            if (~foundIndex) {
              /** replace if the value is found */
              v.Filters.splice(foundIndex, 1, filterValue);
            } else if (Value || ValueFrom || ValueTo) {
              /** insert the value in the filter array */
              v.Filters.push(filterValue);
            }
            v.PageIndex = 0;
            return cloneObject(v);
          });
        },
        [cloneObject, setFilters]
      );

      const onPageChange = useCallback(
        (selected: number) => {
          if (!data.loading) {
            changeFilters({
              PageIndex: selected,
            });
          }
        },
        [changeFilters, data.loading]
      );

      /** getting data when filters change */
      useEffect(() => {
        const filterToUse: UiTableInnerFiltersType = {
          PageSize: memoizedFilters.PageSize,
          PageIndex: memoizedFilters.PageIndex,
          SortByProperty: memoizedFilters.SortByProperty,
          SortDirection: memoizedFilters.SortDirection,
          Filters: memoizedFilters.Filters,
          CheckboxFilters: [],
          OnlyFlagged: memoizedFilters.OnlyFlagged,
        };

        if (doSetSearchParams === true && doPushHistoryRef.current) {
          setSearchParam(
            {
              ...filterToUse,
              PageIndex: filterToUse.PageIndex + 1,
            },
            false,
            saveHistory
          );
        }

        if (
          !notCallSubmitWhenPropertyIsEmpty ||
          (notCallSubmitWhenPropertyIsEmpty && !isEmpty(filterToUse[notCallSubmitWhenPropertyIsEmpty]))
        ) {
          getData(filterToUse);
        }
      }, [doSetSearchParams, memoizedFilters, getData, saveHistory, notCallSubmitWhenPropertyIsEmpty]);

      /** error handling */
      useEffect(() => {
        if (data.error) {
          showAnyErrors(data.error);
        } else if (data.loading && !disabledScrollToTop) {
          scrollWindowToTop();
        }
      }, [data.error, data.loading, disabledScrollToTop]);

      useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
          if (data.response?.Items.length) {
            const keys = data.response.Items.map((item) => item[itemKeyProp]).filter((key) =>
              key === 0 ? true : Boolean(key)
            );

            /** warn if itemKeyProp is not set or is set wrongly */
            invariant(keys.length, 'Set "itemKeyProp" for this table');

            if (keys.length) {
              const objKeys = new Set();

              keys.forEach((key) => {
                objKeys.add(key);
              });

              /** warn if itemKeyProp is not but keys are duplicated */
              invariant(
                objKeys.size === keys.length,
                '"itemKeyProp" is probably set wrongly as it generates duplicated keys for table'
              );
            }
          }
        }
      }, [data.response?.Items, itemKeyProp]);

      const onOuterClick = useCallback(() => {
        setOpenMenuId(null);
      }, []);

      /** handle outer click to close the filter */
      useClickOutside(!!openMenuId && !manageActions, onOuterClick);

      const isEmptyQueryFilters = !Object.entries(getSearchParams()).filter(([key]) => filterProperties.includes(key))
        .length;

      /** reset the filter when query filters do not exist */
      useEffect(() => {
        if (isEmptyQueryFilters) {
          resetFilters();
        }
      }, [isEmptyQueryFilters, resetFilters]);

      /** on tr hover/blur when actions must be toggled (appearance) on hover/blur */
      const onManagedActionToggle = useCallback(
        (id: number | null) => () => {
          if (manageActions) {
            setOpenMenuId(id);
          }
        },
        [manageActions]
      );

      const onItemOpenMenu = useCallback(
        (id: number) => () => {
          setOpenMenuId(id);
        },
        []
      );

      const colSpan = actions ? localColumns.length + 1 : localColumns.length;

      const Pagination = (
        <UiPagination
          currentPage={memoizedFilters.PageIndex}
          loading={data.loading}
          onPageChange={onPageChange}
          perPage={itemsPerPage}
          totalAmount={numberOrDefaultNumber(data.response?.TotalRecordsCount)}
          v2={v2}
        />
      );

      const onTrClickLocal =
        (item: SafeAnyType) =>
        ({ target }: SafeAnyType) => {
          if (isFunction(onTrClick)) {
            /** check if clicked on checkbox in the table */
            if (!target?.classList?.contains('checkbox-col')) {
              onTrClick(item)();
            }
          }
        };

      const emptyRowsToAdd: number[] = useMemo(() => {
        const length = numberOrDefaultNumber(data.response?.Items.length);

        if (dummyTable && length > 0) {
          if (length > itemsPerPage) {
            return [];
          }

          return [...Array(itemsPerPage - length).keys()];
        }
        return [];
      }, [data.response?.Items.length, dummyTable, itemsPerPage]);

      /** need to display actions for devices if the hover function is not supported */
      const isNotSupportedHover = useMediaQuery('(hover: none)');

      const tableComponent = (
        <div
          className={classNames('table-scroller', wrapperClassName, {
            'table-scroller-v2': v2,
            [styles.tableWrapper]: v2,
          })}
        >
          <Table striped bordered={!v2} hover className={classNames('table-condensed', className)}>
            <thead>
              <tr>
                {localColumns.map((columnRaw) => {
                  const columnData = uiTableGetColumnDataWithItem(columnRaw, null);

                  if (columnData.visible === false) {
                    return null;
                  }

                  const {
                    title,
                    filter,
                    className,
                    sortPropertyName,
                    header,
                    popoverText,
                    isCheckBox,
                    onCheckboxClick,
                    isChecked,
                    disabledTH,
                  } = columnData;

                  return (
                    <th key={title || getUniqueId()} className={classNames('cursor-pointer', className)}>
                      {popoverText ? (
                        <UiTextTooltip text={popoverText} wrapperClassName="d-inline-flex" showHover>
                          {title}
                        </UiTextTooltip>
                      ) : (
                        <>
                          {isCheckBox ? (
                            <UiTableCheckbox
                              onCheckboxClick={onCheckboxClick}
                              isChecked={isChecked}
                              disabled={data.loading || disabledTH}
                              thead
                            />
                          ) : (
                            title
                          )}
                        </>
                      )}

                      <UiFilterPopover
                        filter={filter}
                        header={header}
                        formProps={formProps}
                        onSubmit={onSubmitFilter}
                        filters={memoizedFilters.Filters}
                        v2={v2}
                        className={styles.popover}
                      />

                      <UiTableColumnSort
                        name={sortPropertyName}
                        filters={memoizedFilters}
                        onChange={changeFilters}
                        v2={v2}
                      />
                    </th>
                  );
                })}
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                {actions && <th className="manage" />}
              </tr>
            </thead>
            <tbody>
              {data.loading ? (
                <UiTableLoadingPartial colSpan={colSpan} dummyTable={dummyTable} v2={v2} />
              ) : (
                <>
                  {data.response?.Items.length ? (
                    data.response.Items.map((item) => (
                      <tr
                        key={item[itemKeyProp]}
                        onMouseEnter={onManagedActionToggle(item[itemKeyProp])}
                        onMouseLeave={onManagedActionToggle(null)}
                        className={trClassName}
                        onClick={onTrClickLocal(item)}
                      >
                        {localColumns.map((columnRaw) =>
                          UiTableCreateTH(item)(columnRaw, {
                            itemKeyProp,
                          })
                        )}

                        {actions && (
                          <UiTableActionsPartial
                            // key={openMenuId}
                            manage={manageActions}
                            actions={actions}
                            item={item}
                            open={isNotSupportedHover || openMenuId === item[itemKeyProp] || v2}
                            onOpen={onItemOpenMenu(item[itemKeyProp])}
                          />
                        )}
                      </tr>
                    ))
                  ) : (
                    <>
                      {fullTableContent ? (
                        fullTableContent({ colSpan })
                      ) : (
                        <UiTableNotFoundPartial
                          colSpan={colSpan}
                          dummyTable={dummyTable}
                          notFoundText={notFoundText}
                          v2={v2}
                        />
                      )}
                    </>
                  )}

                  {/** paint rows until itemsPerPage in case when is not enough data */}
                  {emptyRowsToAdd.map((index) => (
                    <tr key={index}>
                      <td colSpan={colSpan} />
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        </div>
      );

      return (
        <>
          {wrapperTableScrollerClassName ? (
            <div className={wrapperTableScrollerClassName}>{tableComponent}</div>
          ) : (
            tableComponent
          )}

          {footer ? (
            <div className="table-row-bottom">
              <div className="pull-left">{footer}</div>
              {Pagination}
            </div>
          ) : (
            Pagination
          )}
        </>
      );
    }
  )
);
